import { useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

interface CartResizeHandleProps {
  onResize: (width: number) => void;
  onResizeEnd?: (width: number) => void;
}

export default function CartResizeHandle({ onResize, onResizeEnd }: CartResizeHandleProps) {
  const { t } = useTranslation();
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
    startX.current = e.clientX;
    const sidebar = (e.target as HTMLElement).parentElement;
    startWidth.current = sidebar?.getBoundingClientRect().width ?? 0;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    isDragging.current = true;
    startX.current = touch.clientX;
    const sidebar = (e.target as HTMLElement).parentElement;
    startWidth.current = sidebar?.getBoundingClientRect().width ?? 0;
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    const handleMove = (clientX: number) => {
      if (!isDragging.current) return;
      const delta = startX.current - clientX;
      const newWidth = Math.round(startWidth.current + delta);
      onResize(newWidth);
    };

    const handleMouseMove = (e: MouseEvent) => handleMove(e.clientX);
    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches[0]) handleMove(e.touches[0].clientX);
    };

    const endDrag = () => {
      if (!isDragging.current) return;
      isDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      const sidebar = document.getElementById('pos-cart-sidebar');
      if (sidebar && onResizeEnd) {
        onResizeEnd(sidebar.getBoundingClientRect().width);
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', endDrag);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', endDrag);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', endDrag);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', endDrag);
    };
  }, [onResize, onResizeEnd]);

  return (
    <div
      role="separator"
      aria-label={t('pos_tabs.resize_aria')}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      className="absolute left-0 top-0 bottom-0 w-5 -translate-x-1/2 z-10 cursor-col-resize flex items-center justify-center group"
    >
      <div className="flex flex-col items-center justify-center gap-0.5 px-1 py-2 rounded-full bg-muted/80 border border-border shadow-sm group-hover:bg-primary/10 group-hover:border-primary/30 group-active:bg-primary/20 transition-colors">
        <svg width="6" height="10" viewBox="0 0 6 10" fill="none" className="text-muted-foreground group-hover:text-primary transition-colors">
          <path d="M5 1L1 5L5 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <div className="w-0.5 h-6 rounded-full bg-border group-hover:bg-primary/50 transition-colors" />
        <svg width="6" height="10" viewBox="0 0 6 10" fill="none" className="text-muted-foreground group-hover:text-primary transition-colors">
          <path d="M1 1L5 5L1 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
  );
}
